import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export interface PhaserParameters {
  readonly centerFrequencyHz: number;
  readonly sweepOctaves: number;
  readonly rateHz: number;
  readonly lfoDepth: number;
  readonly envelopeDepth: number;
  readonly envelopeAttackMs: number;
  readonly envelopeReleaseMs: number;
  readonly feedback: number;
  readonly mix: number;
  readonly smoothingSeconds: number;
}
export interface PhaserOptions extends Partial<PhaserParameters> { readonly sampleRate: number; }

type ParameterKey = keyof PhaserParameters;
type ParameterId = 'center-frequency-hz' | 'sweep-octaves' | 'rate-hz' | 'lfo-depth' | 'envelope-depth' | 'envelope-attack-ms' | 'envelope-release-ms' | 'feedback' | 'mix' | 'smoothing-seconds';

export const PHASER_STAGES = 6;
export const PHASER_PARAMETER_BOUNDS = Object.freeze({
  centerFrequencyHz: Object.freeze({ min: 20, max: 40_000, unit: 'Hz' }),
  sweepOctaves: Object.freeze({ min: 0, max: 6, unit: 'oct' }),
  rateHz: Object.freeze({ min: 0.01, max: 20, unit: 'Hz' }),
  lfoDepth: Object.freeze({ min: -1, max: 1, unit: 'ratio' }),
  envelopeDepth: Object.freeze({ min: -1, max: 1, unit: 'ratio' }),
  envelopeAttackMs: Object.freeze({ min: 0.1, max: 500, unit: 'ms' }),
  envelopeReleaseMs: Object.freeze({ min: 1, max: 2_000, unit: 'ms' }),
  feedback: Object.freeze({ min: -0.95, max: 0.95, unit: 'ratio' }),
  mix: Object.freeze({ min: 0, max: 1, unit: 'ratio' }),
  smoothingSeconds: Object.freeze({ min: 0, max: 1, unit: 's' }),
} as const);
export const DEFAULT_PHASER_PARAMETERS: Readonly<PhaserParameters> = Object.freeze({
  centerFrequencyHz: 800, sweepOctaves: 2.5, rateHz: 0.35, lfoDepth: 1,
  envelopeDepth: 0, envelopeAttackMs: 10, envelopeReleaseMs: 120,
  feedback: 0.35, mix: 0.5, smoothingSeconds: 0.01,
});
const ID_TO_KEY: Readonly<Record<ParameterId, ParameterKey>> = Object.freeze({
  'center-frequency-hz': 'centerFrequencyHz', 'sweep-octaves': 'sweepOctaves',
  'rate-hz': 'rateHz', 'lfo-depth': 'lfoDepth', 'envelope-depth': 'envelopeDepth',
  'envelope-attack-ms': 'envelopeAttackMs', 'envelope-release-ms': 'envelopeReleaseMs',
  feedback: 'feedback', mix: 'mix', 'smoothing-seconds': 'smoothingSeconds',
});
const PARAMETER_KEYS = Object.freeze(Object.keys(PHASER_PARAMETER_BOUNDS) as ParameterKey[]);
const SMOOTHED_KEYS = Object.freeze(PARAMETER_KEYS.filter((key) => key !== 'smoothingSeconds'));
const MAX_INTERNAL = 4;
const DENORMAL = 1e-30;
const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(maximum, value));
const flush = (value: number): number => Math.abs(value) < DENORMAL ? 0 : value;
const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
};

/** Coefficient for H(z) = (z^-1 - a) / (1 - a z^-1); its pole is a. */
export const allPassCoefficient = (frequencyHz: number, sampleRate: number): number => {
  assertRange('sampleRate', sampleRate, 1, 768_000);
  assertRange('frequencyHz', frequencyHz, 0, sampleRate * 0.5);
  const tangent = Math.tan(Math.PI * clamp(frequencyHz, 1e-9, sampleRate * 0.499_999) / sampleRate);
  return (1 - tangent) / (1 + tangent);
};

/** Allocation-free mono six-stage phaser. State and modulation are sample based. */
export class PhaserProcessor {
  readonly sampleRate: number;
  private readonly target: PhaserParameters;
  private readonly current: PhaserParameters;
  private readonly delays = new Float64Array(PHASER_STAGES);
  private phase = 0;
  private envelope = 0;
  private feedbackSample = 0;

  constructor(options: PhaserOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000); this.sampleRate = options.sampleRate;
    this.target = { ...DEFAULT_PHASER_PARAMETERS, ...options };
    for (const key of PARAMETER_KEYS) { const b = PHASER_PARAMETER_BOUNDS[key]; assertRange(key, this.target[key], b.min, b.max); }
    this.current = { ...this.target };
  }

  reset(): void {
    this.delays.fill(0); this.phase = 0; this.envelope = 0; this.feedbackSample = 0;
    for (const key of PARAMETER_KEYS) (this.current as { -readonly [K in ParameterKey]: number })[key] = this.target[key];
  }

  setParameter(id: string, value: number): void {
    const key = ID_TO_KEY[id as ParameterId]; if (!key) throw new RangeError(`unknown parameter: ${id}`);
    const bounds = PHASER_PARAMETER_BOUNDS[key]; assertRange(id, value, bounds.min, bounds.max);
    (this.target as { -readonly [K in ParameterKey]: number })[key] = value;
    if (key === 'smoothingSeconds') (this.current as { -readonly [K in ParameterKey]: number })[key] = value;
  }

  processInto(input: Float32Array, output: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || output.length < frames) throw new RangeError('buffers are too short for frames');
    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(input[frame]!)) throw new RangeError('input samples must be finite');
    }
    for (let frame = 0; frame < frames; frame += 1) {
      const dry = input[frame]!;
      this.advanceParameters();
      const level = Math.min(1, Math.abs(dry));
      const envelopeMs = level > this.envelope ? this.current.envelopeAttackMs : this.current.envelopeReleaseMs;
      const envelopeCoefficient = Math.exp(-1 / (envelopeMs * 0.001 * this.sampleRate));
      this.envelope = flush(envelopeCoefficient * this.envelope + (1 - envelopeCoefficient) * level);
      const lfo = Math.sin(2 * Math.PI * this.phase);
      this.phase += this.current.rateHz / this.sampleRate; this.phase -= Math.floor(this.phase);
      const modulation = this.current.sweepOctaves * (this.current.lfoDepth * lfo + this.current.envelopeDepth * this.envelope);
      const frequency = clamp(this.current.centerFrequencyHz * 2 ** modulation, 10, this.sampleRate * 0.45);
      const coefficient = allPassCoefficient(frequency, this.sampleRate);
      let wet = clamp(dry + this.current.feedback * this.feedbackSample, -MAX_INTERNAL, MAX_INTERNAL);
      for (let stage = 0; stage < PHASER_STAGES; stage += 1) {
        const next = -coefficient * wet + this.delays[stage]!;
        this.delays[stage] = flush(clamp(wet + coefficient * next, -MAX_INTERNAL, MAX_INTERNAL));
        wet = clamp(next, -MAX_INTERNAL, MAX_INTERNAL);
      }
      this.feedbackSample = flush(wet);
      const mixed = clamp(dry * (1 - this.current.mix), -MAX_INTERNAL, MAX_INTERNAL) + wet * this.current.mix;
      output[frame] = flush(clamp(Number.isFinite(mixed) ? mixed : 0, -MAX_INTERNAL, MAX_INTERNAL));
    }
  }

  private advanceParameters(): void {
    const seconds = this.current.smoothingSeconds;
    const amount = seconds === 0 ? 1 : 1 - Math.exp(-1 / (seconds * this.sampleRate));
    for (const key of SMOOTHED_KEYS) {
      const value = this.current[key] + (this.target[key] - this.current[key]) * amount;
      (this.current as { -readonly [K in ParameterKey]: number })[key] = Math.abs(value - this.target[key]) < 1e-12 ? this.target[key] : value;
    }
  }
}

class PhaserModuleInstance implements DspModuleInstance {
  private readonly processor: PhaserProcessor;
  constructor(sampleRate: number, private readonly maxBlockSize: number) { this.processor = new PhaserProcessor({ sampleRate }); }
  reset(): void { this.processor.reset(); }
  setParameter(id: string, value: number): void { this.processor.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const input = block.inputs.in; const output = block.outputs.out;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.processor.processInto(input, output, block.frames);
  }
}

export const mod029 = defineDspModule({
  descriptor: {
    id: 'mir.mod-029', name: 'Phaser all-pass network', version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }], outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'center-frequency-hz', defaultValue: 800, minValue: 20, maxValue: 40_000, unit: 'Hz' },
      { id: 'sweep-octaves', defaultValue: 2.5, minValue: 0, maxValue: 6, unit: 'oct' },
      { id: 'rate-hz', defaultValue: 0.35, minValue: 0.01, maxValue: 20, unit: 'Hz' },
      { id: 'lfo-depth', defaultValue: 1, minValue: -1, maxValue: 1, unit: 'ratio' },
      { id: 'envelope-depth', defaultValue: 0, minValue: -1, maxValue: 1, unit: 'ratio' },
      { id: 'envelope-attack-ms', defaultValue: 10, minValue: 0.1, maxValue: 500, unit: 'ms' },
      { id: 'envelope-release-ms', defaultValue: 120, minValue: 1, maxValue: 2_000, unit: 'ms' },
      { id: 'feedback', defaultValue: 0.35, minValue: -0.95, maxValue: 0.95, unit: 'ratio' },
      { id: 'mix', defaultValue: 0.5, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'smoothing-seconds', defaultValue: 0.01, minValue: 0, maxValue: 1, unit: 's' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid DSP instance context');
    return new PhaserModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
